import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import {
  X,
  Upload,
  Settings,
  Tag,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react'
import Button from '../ui/Button'
import Input from '../ui/Input'
import Card from '../ui/Card'
import { useTMSProjects } from '../../hooks/useTMSProjects'
import JobSelector from './JobSelector'
import api from '../../utils/api'

const ImportJobModal = ({ isOpen, onClose, onSuccess, preSelectedJobId, preSelectedJobTitle }) => {
  const { t } = useTranslation()
  const { projects, defaultProjectId } = useTMSProjects()

  // State declarations first
  const [selectedProject, setSelectedProject] = useState(defaultProjectId || '')
  const [selectedJob, setSelectedJob] = useState(preSelectedJobId ? {
    uuid: preSelectedJobId,
    original_filename: preSelectedJobTitle
  } : null)
  const [importSettings, setImportSettings] = useState({
    tags: [],
    overwrite: null, // null means use project default
    flag_for_review: null,
    create_missing_locales: null
  })
  const [newTag, setNewTag] = useState('')
  const [errors, setErrors] = useState({})
  const [step, setStep] = useState(preSelectedJobId ? 'configure' : 'select') // 'select' or 'configure'

  // Update step and selectedJob when preSelectedJobId changes
  React.useEffect(() => {
    if (preSelectedJobId) {
      setSelectedJob({
        uuid: preSelectedJobId,
        original_filename: preSelectedJobTitle
      })
      setStep('configure')
    } else {
      setSelectedJob(null)
      setStep('select')
    }
  }, [preSelectedJobId, preSelectedJobTitle])

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (importData) => {
      const response = await api.post(`/tms/user/projects/${selectedProject}/import`, {
        job_uuid: selectedJob.uuid,
        tags: importSettings.tags,
        overwrite: importSettings.overwrite,
        flag_for_review: importSettings.flag_for_review,
        create_missing_locales: importSettings.create_missing_locales
      })
      return response.data
    },
    onSuccess: (data) => {
      onSuccess?.(data)
      handleClose()
    },
    onError: (error) => {
      setErrors({
        submit: error.response?.data?.detail || t('tms.importJobFailed')
      })
    }
  })

  const handleClose = () => {
    setSelectedProject(defaultProjectId || '')
    setSelectedJob(preSelectedJobId ? {
      uuid: preSelectedJobId,
      original_filename: preSelectedJobTitle
    } : null)
    setImportSettings({
      tags: [],
      overwrite: null,
      flag_for_review: null,
      create_missing_locales: null
    })
    setNewTag('')
    setErrors({})
    const resetStep = preSelectedJobId ? 'configure' : 'select'
    setStep(resetStep)
    onClose()
  }

  const handleJobSelect = (job) => {
    setSelectedJob(job)
    setStep('configure')
  }

  const handleBackToJobSelection = () => {
    setSelectedJob(null)
    setStep('select')
    setErrors({})
  }

  const handleAddTag = () => {
    if (newTag.trim() && !importSettings.tags.includes(newTag.trim())) {
      setImportSettings(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove) => {
    setImportSettings(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!selectedProject) {
      setErrors({ project: t('validation.required') })
      return
    }

    if (!selectedJob?.uuid) {
      setErrors({ job: t('tms.jobIdRequired') })
      return
    }

    setErrors({})
    importMutation.mutate()
  }

  const selectedProjectConfig = projects.find(p => p.project_id === selectedProject)

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div data-testid="import-job-modal" className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            {step === 'configure' && !preSelectedJobId && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToJobSelection}
                className="mr-3 p-1"
              >
                ← Back
              </Button>
            )}
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {step === 'select' ? t('tms.selectJobToImport') : t('tms.importJobToTMS')}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="p-1"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        {step === 'select' ? (
          <div className="p-6">
            <JobSelector
              onJobSelect={handleJobSelect}
              selectedJobId={selectedJob?.uuid}
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Job Information */}
            <Card className="bg-gray-50 dark:bg-gray-700">
              <Card.Content className="p-4">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                  {t('tms.jobToImport')}
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  <div><strong>{t('tms.jobId')}:</strong> {selectedJob?.uuid}</div>
                  {selectedJob?.original_filename && (
                    <div><strong>{t('tms.title')}:</strong> {selectedJob.original_filename}</div>
                  )}
                  {selectedJob?.source_language && (
                    <div><strong>{t('common.sourceLanguage')}:</strong> {selectedJob.source_language}</div>
                  )}
                  {selectedJob?.target_language && (
                    <div><strong>{t('common.targetLanguage')}:</strong> {selectedJob.target_language}</div>
                  )}
                </div>
              </Card.Content>
            </Card>

          {/* Project Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tms.targetProject')} *
            </label>
            <select
              data-testid="project-selector"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            >
              <option value="">{t('tms.selectProject')}</option>
              {projects.map((project) => (
                <option key={project.project_id} value={project.project_id}>
                  {project.name} ({project.project_id})
                  {project.project_id === defaultProjectId ? ` - ${t('tms.default')}` : ''}
                </option>
              ))}
            </select>
            {errors.project && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.project}</p>
            )}
          </div>

          {/* Project Configuration Preview */}
          {selectedProjectConfig && (
            <Card className="bg-blue-50 dark:bg-blue-900/20" data-testid="project-defaults">
              <Card.Content className="p-4">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                  {t('tms.projectDefaults')}
                </h4>
                <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                  <div>
                    <strong>{t('tms.overwrite')}:</strong> {
                      selectedProjectConfig.default_overwrite_translations ? t('common.yes') : t('common.no')
                    }
                  </div>
                  <div>
                    <strong>{t('tms.flagForReview')}:</strong> {
                      selectedProjectConfig.default_flag_for_review ? t('common.yes') : t('common.no')
                    }
                  </div>
                  <div>
                    <strong>{t('tms.createLocales')}:</strong> {
                      selectedProjectConfig.create_missing_locales ? t('common.yes') : t('common.no')
                    }
                  </div>
                  {selectedProjectConfig.default_tags?.length > 0 && (
                    <div>
                      <strong>{t('tms.defaultTags')}:</strong> {selectedProjectConfig.default_tags.join(', ')}
                    </div>
                  )}
                </div>
              </Card.Content>
            </Card>
          )}

          {/* Custom Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tms.additionalTags')}
            </label>

            {/* Existing Tags */}
            {importSettings.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {importSettings.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm rounded"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 text-blue-500 hover:text-blue-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add Tag Input */}
            <div className="flex space-x-2">
              <Input
                data-testid="additional-tags-input"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={t('tms.addTagPlaceholder')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTag()
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
              >
                <Tag className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('tms.tagsWillBeAddedToDefaults')}
            </p>
          </div>

          {/* Import Settings Override */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('tms.importSettingsOverride')}
            </h4>
            <div className="space-y-2 text-sm">
              <label className="flex items-center">
                <input
                  data-testid="overwrite-existing-checkbox"
                  type="checkbox"
                  checked={importSettings.overwrite === true}
                  onChange={(e) => setImportSettings(prev => ({
                    ...prev,
                    overwrite: e.target.checked ? true : null
                  }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                />
                {t('tms.forceOverwriteTranslations')}
              </label>
              <label className="flex items-center">
                <input
                  data-testid="flag-for-review-checkbox"
                  type="checkbox"
                  checked={importSettings.flag_for_review === true}
                  onChange={(e) => setImportSettings(prev => ({
                    ...prev,
                    flag_for_review: e.target.checked ? true : null
                  }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                />
                {t('tms.forceFlagForReview')}
              </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('tms.uncheckedUseProjectDefaults')}
            </p>
          </div>

          {/* Errors */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-center">
                <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={importMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              data-testid="start-import-button"
              type="submit"
              loading={importMutation.isPending}
              disabled={importMutation.isPending || !selectedProject}
              className="flex items-center"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('tms.startImport')}
            </Button>
          </div>
        </form>
        )}
      </div>
    </div>
  )
}

export default ImportJobModal